import React, { useEffect, useState } from 'react';
import axios from './axiosSetup';
import API_BASE from './apiBase';

const getSsoLoginUrl = () => {
  const base = (API_BASE || '').replace(/\/+$/, '');
  return `${base}${base.endsWith('/api') ? '/v1/sso-login' : '/api/v1/sso-login'}`;
};

const inspectTokenForMessage = (value) => {
  try {
    const payloadPart = value.split('.')[1];
    if (!payloadPart) return null;
    const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp && Number(payload.exp) <= Math.floor(Date.now() / 1000)) {
      return 'This SSO token has expired. Start a new login in OneDPDP and paste the fresh token here.';
    }
    if (payload.type && payload.type !== 'sso') {
      return 'This is not an incoming OneDPDP SSO token. Paste the original token with type="sso".';
    }
    return null;
  } catch (_) {
    return null;
  }
};

function LocalSsoLogin({ onLogin }) {
  const [token, setToken] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('token') || '';
    } catch (_) {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('token')) {
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    } catch (_) {
      // Keep the token available in state if URL cleanup is not supported.
    }
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    const value = token.trim();
    if (!value) {
      setError('Token is required.');
      return;
    }
    const tokenHint = inspectTokenForMessage(value);
    if (tokenHint) {
      setError(tokenHint);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(getSsoLoginUrl(), {
        params: { token: value, json: 1 },
        withCredentials: true,
        skipRedirectOn401: true,
        timeout: 10000,
      });
      const data = response?.data;
      if (!data?.success || !data?.token || !data?.user) {
        throw new Error(data?.error || 'SSO login failed.');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('last_active_at', String(Date.now()));
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user.tenant_id != null) {
        localStorage.setItem('tenant_id', String(data.user.tenant_id));
      }
      if (onLogin) {
        onLogin({ token: data.token, user: data.user, tenant_id: data.user.tenant_id });
      }
    } catch (requestError) {
      console.error('Local SSO login failed:', {
        status: requestError?.response?.status || null,
        detail: requestError?.response?.data?.error || requestError?.response?.data?.detail || requestError?.message || null,
      });
      const serverMessage = requestError?.response?.data?.error || requestError?.response?.data?.detail;
      setError(serverMessage || (requestError?.response?.status === 401
        ? 'Token invalid or expired. Start a new login in OneDPDP and paste the fresh SSO token.'
        : requestError?.message || 'SSO login failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ maxWidth: 720, margin: '80px auto', padding: 24 }}>
      <h1>Local SSO Login</h1>
      <p>Paste a fresh raw SSO JWT copied from OneDPDP. Do not use an old token or a portal token.</p>
      <form onSubmit={submit}>
        <label htmlFor="local-sso-token">SSO token</label>
        <textarea
          id="local-sso-token"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          rows={8}
          style={{ display: 'block', width: '100%', margin: '12px 0', fontFamily: 'monospace' }}
          autoFocus
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign in with SSO token'}
        </button>
        {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
      </form>
    </main>
  );
}

export default LocalSsoLogin;